import { Link } from "react-router";
import { useOrders } from "@/lib/hooks";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorMessage } from "@/components/ErrorMessage";
import {
  formatCurrency,
  formatDate,
  formatOrderStatus,
  formatPaymentMethod,
} from "@/lib/formatters";
import type { Order } from "@/lib/api";

function statusBadgeClasses(status: Order["status"]): string {
  switch (status) {
    case "paid":
      return "bg-green-100 text-green-800";
    case "processing":
      return "bg-blue-100 text-blue-800";
    case "completed":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

export function OrderHistoryPage(): React.ReactElement {
  const { data, isLoading, error } = useOrders();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorMessage
        message={
          error instanceof Error ? error.message : "Failed to load orders"
        }
      />
    );
  }

  const orders = data?.orders ?? [];

  if (orders.length === 0) {
    return (
      <div className="py-12 text-center">
        <h2 className="mb-2 text-lg font-semibold text-gray-900">
          No orders yet
        </h2>
        <p className="mb-4 text-sm text-gray-500">
          Place your first order to get started.
        </p>
        {/* The page is protected, so the viewer is logged in: the roster's
            new order, as the logged-in nav and ConfirmationPage use. */}
        <Link
          to="/roster/new-order"
          className="inline-block rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Place an Order
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[800px]">
      <h1 className="mb-6 text-2xl font-bold text-gray-900">Receipts</h1>

      {/* Desktop table */}
      <div className="hidden md:block">
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="px-4 py-3 font-medium text-gray-700">
                  Order ID
                </th>
                <th className="px-4 py-3 font-medium text-gray-700">
                  Order Date
                </th>
                <th className="px-4 py-3 font-medium text-gray-700">
                  # Employees
                </th>
                <th className="px-4 py-3 font-medium text-gray-700">Total</th>
                <th className="px-4 py-3 font-medium text-gray-700">
                  Payment
                </th>
                <th className="px-4 py-3 font-medium text-gray-700">Status</th>
                <th className="px-4 py-3 font-medium text-gray-700">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">
                    {order.id.slice(0, 8)}
                  </td>
                  <td className="px-4 py-3 text-gray-900">
                    {formatDate(order.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-gray-900">{order.quantity}</td>
                  <td className="px-4 py-3 text-gray-900">
                    {formatCurrency(order.totalAmount)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatPaymentMethod(order.paymentMethod)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClasses(order.status)}`}
                    >
                      {formatOrderStatus(order.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/orders/${order.id}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 md:hidden">
        {orders.map((order) => (
          <Link
            key={order.id}
            to={`/orders/${order.id}`}
            className="block rounded-lg border border-gray-200 bg-white p-4 shadow-sm hover:border-gray-300"
          >
            <div className="mb-1 font-mono text-xs text-gray-400">
              {order.id.slice(0, 8)}
            </div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900">
                {formatDate(order.createdAt)}
              </span>
              <span
                className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClasses(order.status)}`}
              >
                {formatOrderStatus(order.status)}
              </span>
            </div>
            <div className="space-y-1 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Employees</span>
                <span className="text-gray-900">{order.quantity}</span>
              </div>
              <div className="flex justify-between">
                <span>Total</span>
                <span className="font-medium text-gray-900">
                  {formatCurrency(order.totalAmount)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Payment</span>
                <span className="text-gray-900">
                  {formatPaymentMethod(order.paymentMethod)}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
